"use client";

import { Instagram, MapPin, Phone, Youtube, Activity } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => <li key={i} data-index={i}>
            <div className="h-5 w-32 bg-[hsl(45_100%_50%/0.1)] animate-pulse rounded-md" />
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group inline-flex items-center text-[hsl(210_20%_98%/0.75)] hover:text-[hsl(45_100%_50%)] font-default font-medium text-[15px] transition-colors duration-200">
            <span className="relative">
              {item.label}
              <span className="absolute left-0 -bottom-0.5 w-0 h-[1.5px] bg-[hsl(45_100%_50%)] group-hover:w-full transition-all duration-220" />
            </span>
          </Link>
        </li>)}
    </ul>;
}
const tickerItems = ["RESILIENCE", "EMPATHY", "LEADERSHIP", "MINDSET", "COMMAND", "IMPACT"];
const socialLinks = [{
  name: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  name: "TikTok",
  icon: Activity,
  href: "#"
}, {
  name: "YouTube",
  icon: Youtube,
  href: "#"
}];
const getStartedLinks = [{
  label: "Speaker Sheet",
  to: "#"
}, {
  label: "Keynote Topics",
  to: "/DamienColePage"
}, {
  label: "Booking Availability",
  to: "#"
}, {
  label: "About Damien",
  to: "/AboutPage"
}];
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section data-section-id="1144" id="footer" ref={ref} className="relative overflow-hidden bg-[hsl(215_25%_10%)] text-[hsl(210_20%_98%)] pt-20 pb-10 border-t border-white/10">
      {}
      <div className="pointer-events-none absolute top-12 right-12 opacity-30" aria-hidden="true">
        <svg width="180" height="120" viewBox="0 0 180 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          {}
          <g stroke="hsl(45 100% 50%)" strokeWidth="2" strokeLinecap="round">
            <line x1="20" y1="20" x2="20" y2="8" />
            <line x1="20" y1="32" x2="20" y2="44" />
            <line x1="8" y1="26" x2="20" y2="26" />
            <line x1="20" y1="26" x2="32" y2="26" />
          </g>
          {}
          <g stroke="hsl(45 100% 50%)" strokeWidth="2" strokeLinecap="round">
            <line x1="100" y1="50" x2="100" y2="38" />
            <line x1="100" y1="62" x2="100" y2="74" />
            <line x1="88" y1="56" x2="112" y2="56" />
          </g>
          {}
          <g stroke="hsl(45 100% 50%)" strokeWidth="2" strokeLinecap="round">
            <line x1="155" y1="90" x2="155" y2="80" />
            <line x1="155" y1="98" x2="155" y2="108" />
            <line x1="147" y1="94" x2="163" y2="94" />
          </g>
          {}
          <path d="M 40 80 L 100 80" stroke="hsl(45 100% 50%)" strokeWidth="1" fill="none" strokeLinecap="round" />
        </svg>
      </div>

      <div className="relative max-w-[1320px] mx-auto px-6 md:px-10 lg:px-16">
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          {}
          <div className="lg:col-span-4 space-y-5">
            <div className="flex items-center gap-3">
              <Link to="/" className="inline-flex items-center">
                <WvcLogo className="h-8 w-auto" />
              </Link>
              {}
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="shrink-0">
                {}
                <path d="M 8 36 L 50 32 L 50 44 L 8 44 Z" fill="hsl(45 100% 50%)" />
                {}
                <rect x="12" y="20" width="8" height="8" fill="hsl(210 20% 98%)" />
                <rect x="22" y="18" width="6" height="6" fill="hsl(210 20% 98%)" />
                {}
                <path d="M 24 34 L 28 34" stroke="hsl(215 25% 10%)" strokeWidth="2" strokeLinecap="round" fill="none" />
                <path d="M 34 34 L 38 34" stroke="hsl(215 25% 10%)" strokeWidth="2" strokeLinecap="round" fill="none" />
                {}
                <rect x="23" y="37" width="3" height="3" fill="hsl(215 25% 10%)" opacity="0.7" />
                <rect x="37" y="37" width="3" height="3" fill="hsl(215 25% 10%)" opacity="0.7" />
                {}
                <text x="42" y="18" fill="hsl(45 100% 50%)" fontSize="10" fontFamily="var(--font-mono)" fontWeight="600">
                  DC
                </text>
              </svg>
            </div>

            <p className="font-default font-extrabold text-[hsl(210_20%_98%)] text-xl leading-tight max-w-xs uppercase tracking-tighter">
              Leading with Empathy. Commanding with Resilience.
            </p>

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3 text-[hsl(215_15%_60%)] font-default text-[15px]">
                <MapPin className="size-4 mt-0.5 shrink-0 text-[hsl(45_100%_50%)]" strokeWidth={2} />
                <span>Global Keynote Speaker & Leadership Coach</span>
              </div>
              <div className="flex items-start gap-3 text-[hsl(215_15%_60%)] font-default text-[15px]">
                <Phone className="size-4 mt-0.5 shrink-0 text-[hsl(45_100%_50%)]" strokeWidth={2} />
                <span>booking@damien-cole.com</span>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 bg-[hsl(45_100%_50%/0.1)] text-[hsl(45_100%_50%)] font-mono font-bold text-[13px] tracking-widest px-3 py-1.5 rounded-md border border-[hsl(45_100%_50%/0.2)]"><span className="size-2 rounded-sm bg-[hsl(45_100%_50%)]" data-ast-id="f:/src/reusable_sections/Footer.tsx|sig:yymk8a" data-ast-start="6977" data-ast-end="7037"></span>NOW BOOKING Q3-Q4 2026</div>
          </div>

          {}
          <div className="lg:col-span-3 space-y-5">
            <h4 className="font-default font-extrabold text-xl text-[hsl(210_20%_98%)] tracking-tight uppercase">
              Navigation
            </h4>
            <MenuProvider menu_id="3">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="lg:col-span-3 space-y-5">
            <h4 className="font-default font-extrabold text-xl text-[hsl(210_20%_98%)] tracking-tight uppercase">
              Resources
            </h4>
            <ul className="space-y-3">
              {getStartedLinks.map((link, i) => <li key={link.label} data-index={i}>
                  <Link to={link.to} className="group inline-flex items-center text-[hsl(215_15%_60%)] hover:text-[hsl(45_100%_50%)] font-default font-medium text-[15px] transition-colors duration-200">
                    <span className="relative">
                      {link.label}
                      <span className="absolute left-0 -bottom-0.5 w-0 h-[1.5px] bg-[hsl(45_100%_50%)] group-hover:w-full transition-all duration-220" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="lg:col-span-2 space-y-5">
            <h4 className="font-default font-extrabold text-xl text-[hsl(210_20%_98%)] tracking-tight uppercase">
              The Command Stage
            </h4>
            <div className="flex flex-wrap gap-2.5">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.name} to={social.href} data-index={i} aria-label={social.name} className="inline-flex items-center justify-center size-11 rounded-md bg-[hsl(215_20%_15%)] hover:bg-[hsl(45_100%_50%)] text-[hsl(45_100%_50%)] hover:text-[hsl(215_25%_10%)] hover:scale-105 transition-all duration-220 border border-white/5">
                    <Icon className="size-5" strokeWidth={2} />
                  </Link>;
            })}
            </div>
            <p className="font-default text-sm text-[hsl(215_15%_60%)] leading-relaxed">
              Join the inner circle for weekly leadership insights and performance strategies.
            </p>
            <Button asChild className="bg-[hsl(45_100%_50%)] hover:bg-[hsl(210_20%_98%)] text-[hsl(215_25%_10%)] rounded-md font-default font-extrabold tracking-tight px-6 py-2.5 h-auto shadow-lg hover:scale-[1.02] transition-all duration-220 uppercase">
              <Link to="#">Book Damien</Link>
            </Button>
          </div>
        </div>

        {}
        <div className="mt-16 pt-8 border-t border-white/10">
          {}
          <div className="relative overflow-hidden mb-6">
            <div className="flex gap-8 whitespace-nowrap font-mono text-xs text-[hsl(45_100%_50%/0.15)] tracking-[0.2em] uppercase" style={{
            animation: "beach-ticker 40s linear infinite",
            width: "max-content"
          }}>
              {[...Array(8)].map((_, loopIdx) => <div key={loopIdx} data-index={loopIdx} className="flex gap-8 shrink-0">
                  {tickerItems.map((item, i) => <span key={`${loopIdx}-${i}`} className="shrink-0">
                      {item}
                    </span>)}
                  <span className="shrink-0">/</span>
                </div>)}
            </div>
            <style>{`
              @keyframes beach-ticker {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
            `}</style>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <p className="font-default font-semibold text-[13px] tracking-wide text-[hsl(215_15%_60%)]">© 2026 Damien Cole — The Command Stage. All Rights Reserved.</p>
            <div className="flex items-center gap-5">
              <Link to="#" className="font-default text-[13px] text-[hsl(215_15%_60%)] hover:text-[hsl(45_100%_50%)] transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="#" className="font-default text-[13px] text-[hsl(215_15%_60%)] hover:text-[hsl(45_100%_50%)] transition-colors duration-200">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}